import { ReactComponent as BusSvg } from '@/images/bus.svg';
import { ReactComponent as ScooterSvg } from '@/images/scooter.svg';
import { ReactComponent as CarSvg } from '@/images/car.svg';
import { ReactComponent as TruckSvg } from '@/images/truck.svg';
import { ReactComponent as BikeSvg } from '@/images/bike.svg';
import { ReactComponent as PedestrianSvg } from '@/images/pedestrian.svg';
import { ReactComponent as MotorbikeSvg } from '@/images/motorbike.svg';
import type { Profile } from '@/stores/common-store';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from './ui/select';
import { useCallback } from 'react';
import { useCommonStore } from '@/stores/common-store';
import { Loader2 } from 'lucide-react';
import { useSearch } from '@tanstack/react-router';

interface ProfileOption {
  value: Profile;
  label: string;
  icon:
    | 'car'
    | 'bike'
    | 'walk'
    | 'transit'
    | 'truck'
    | 'scooter'
    | 'motorcycle';
}

const PROFILE_GROUPS: Array<{ label: string; profiles: ProfileOption[] }> = [
  {
    label: 'Driving',
    profiles: [
      { value: 'car', label: 'Car', icon: 'car' },
      { value: 'auto_shorter', label: 'Car — shortest', icon: 'car' },
      { value: 'auto_pedestrian', label: 'Drive + park + walk', icon: 'car' },
      { value: 'taxi', label: 'Taxi', icon: 'car' },
      { value: 'hov', label: 'High-occupancy vehicle', icon: 'car' },
      { value: 'truck', label: 'Truck', icon: 'truck' },
      { value: 'bus', label: 'Bus', icon: 'transit' },
      { value: 'motorcycle', label: 'Motorcycle', icon: 'motorcycle' },
      { value: 'motor_scooter', label: 'Motor scooter', icon: 'scooter' },
    ],
  },
  {
    label: 'Walking and cycling',
    profiles: [
      { value: 'bicycle', label: 'Bicycle', icon: 'bike' },
      { value: 'bikeshare', label: 'Walk + bike share', icon: 'bike' },
      { value: 'pedestrian', label: 'Pedestrian', icon: 'walk' },
    ],
  },
  {
    label: 'Public transport',
    profiles: [
      { value: 'transit', label: 'Public transport', icon: 'transit' },
      {
        value: 'multimodal',
        label: 'Walk + public transport',
        icon: 'transit',
      },
    ],
  },
];

const PROFILE_OPTIONS = PROFILE_GROUPS.flatMap((group) => group.profiles);

const ProfileIcon = ({
  icon,
  className = 'size-5',
}: {
  icon: ProfileOption['icon'];
  className?: string;
}) => {
  switch (icon) {
    case 'bike':
      return <BikeSvg className={className} />;
    case 'walk':
      return <PedestrianSvg className={className} />;
    case 'transit':
      return <BusSvg className={className} />;
    case 'truck':
      return <TruckSvg className={className} />;
    case 'scooter':
      return <ScooterSvg className={className} />;
    case 'motorcycle':
      return <MotorbikeSvg className={className} />;
    default:
      return <CarSvg className={className} />;
  }
};

interface ProfilePickerProps {
  loading: boolean;
  onProfileChange: (value: Profile) => void;
}

export const ProfilePicker = ({
  loading,
  onProfileChange,
}: ProfilePickerProps) => {
  const resetSettings = useCommonStore((state) => state.resetSettings);
  const { profile: activeProfile } = useSearch({ from: '/$activeTab' });
  const selectedProfile =
    PROFILE_OPTIONS.find((profile) => profile.value === activeProfile) ??
    PROFILE_OPTIONS[0]!;

  const handleUpdateProfile = useCallback(
    (value: Profile) => {
      if (value === activeProfile) return;
      resetSettings(value);
      onProfileChange(value);
    },
    [activeProfile, resetSettings, onProfileChange]
  );

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="travel-profile"
        className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground"
      >
        Travel profile
      </label>
      <Select
        value={activeProfile}
        onValueChange={(value) => handleUpdateProfile(value as Profile)}
      >
        <SelectTrigger
          id="travel-profile"
          className="h-11 w-full bg-background"
          aria-busy={loading}
          data-testid="profile-select"
        >
          <span className="flex min-w-0 items-center gap-2">
            {loading ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span aria-hidden>
                <ProfileIcon icon={selectedProfile.icon} />
              </span>
            )}
            <span className="truncate">{selectedProfile.label}</span>
          </span>
        </SelectTrigger>
        <SelectContent align="start" className="max-h-[min(28rem,70vh)]">
          {PROFILE_GROUPS.map((group, groupIndex) => (
            <SelectGroup key={group.label}>
              {groupIndex > 0 && <SelectSeparator />}
              <SelectLabel>{group.label}</SelectLabel>
              {group.profiles.map((profile) => (
                <SelectItem
                  key={profile.value}
                  value={profile.value}
                  className="min-h-11"
                  data-testid={`profile-option-${profile.value}`}
                >
                  <span aria-hidden>
                    <ProfileIcon icon={profile.icon} className="size-4" />
                  </span>
                  {profile.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
